import { useEffect, useState, useCallback } from 'react';
import { CalendarClock } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

const HOUR_START = 5;
const HOUR_END = 20;
const TOTAL_MINUTES = (HOUR_END - HOUR_START) * 60;

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 border-blue-400 text-blue-800',
  active: 'bg-green-100 border-green-500 text-green-800',
  completed: 'bg-gray-100 border-gray-300 text-gray-500',
};

const DIRECTION_LABELS = {
  inbound: 'Aller',
  outbound: 'Retour',
  morning: 'Matin',
  afternoon: 'Après-midi',
};

function toPercent(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return Math.max(0, Math.min(100, (((h - HOUR_START) * 60 + m) / TOTAL_MINUTES) * 100));
}

function nowPercent() {
  const now = new Date();
  return Math.max(0, Math.min(100, (((now.getHours() - HOUR_START) * 60 + now.getMinutes()) / TOTAL_MINUTES) * 100));
}

const DayPlanningTimeline = ({ routeId }) => {
  const [slots, setSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(nowPercent);

  const load = useCallback(async () => {
    if (!routeId) return;
    setIsLoading(true);
    setError(null);
    try {
      const r = await unifiedApi.tracking.getDayView({ routeId });
      if (r.success) setSlots(r.data || []);
    } catch (err) {
      logger.error('❌ [DayPlanningTimeline]', err);
      setError('Impossible de charger le planning');
    } finally {
      setIsLoading(false);
    }
  }, [routeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setNow(nowPercent()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!routeId) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-blue-600" />
        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Planning du jour</p>
      </div>

      {isLoading && <p className="text-xs text-gray-400">Chargement…</p>}

      {error && <p className="text-xs text-red-400">{error}</p>}

      {!isLoading && !error && slots.length === 0 && (
        <p className="text-xs text-gray-400">Aucun trajet planifié aujourd'hui.</p>
      )}

      {!isLoading && !error && slots.length > 0 && (
        <div className="space-y-1.5">
          {/* Règle horaire */}
          <div className="flex justify-between px-0.5">
            {Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i)
              .filter((h) => h % 2 === 0 || h === HOUR_START || h === HOUR_END)
              .map((h) => (
                <span key={h} className="text-xs text-gray-300" style={{ width: 0, textAlign: 'center' }}>
                  {String(h).padStart(2, '0')}h
                </span>
              ))}
          </div>

          {/* Rail */}
          <div className="relative h-7 bg-gray-100 rounded-lg overflow-visible">
            {/* Lignes de grille */}
            {Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => i).map((i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 w-px bg-gray-200"
                style={{ left: `${(i / (HOUR_END - HOUR_START)) * 100}%` }}
              />
            ))}

            {/* Créneaux */}
            {slots.map((slot, i) => {
              const left = toPercent(slot.start);
              const width = Math.max(toPercent(slot.end) - left, 1.5);
              const label = DIRECTION_LABELS[slot.direction] || DIRECTION_LABELS[slot.type] || slot.label;
              const style = STATUS_STYLES[slot.status] || STATUS_STYLES.scheduled;

              return (
                <div
                  key={i}
                  title={`${label} · ${slot.start} – ${slot.end}`}
                  className={`absolute top-1 bottom-1 rounded border ${style} flex items-center justify-center overflow-hidden`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                >
                  {width > 7 && <span className="text-[9px] font-bold px-1 truncate">{label}</span>}
                </div>
              );
            })}

            {/* Curseur maintenant */}
            {now > 0 && now < 100 && (
              <div className="absolute top-0 bottom-0 w-0.5 bg-orange-400 z-10" style={{ left: `${now}%` }}>
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-orange-400" />
              </div>
            )}
          </div>

          {/* Légende */}
          <div className="flex items-center gap-3 flex-wrap">
            {[
              { key: 'scheduled', label: 'Planifié', cls: 'bg-blue-100 border-blue-400' },
              { key: 'active', label: 'En cours', cls: 'bg-green-100 border-green-500' },
              { key: 'completed', label: 'Terminé', cls: 'bg-gray-100 border-gray-300' },
            ].map(({ key, label, cls }) => (
              <div key={key} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded border ${cls}`} />
                <span className="text-xs text-gray-500">{label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1">
              <div className="w-0.5 h-3 bg-orange-400 rounded" />
              <span className="text-xs text-gray-500">Maintenant</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayPlanningTimeline;
